// Downscales an image to a small JPEG for the room-photo pre-check. The
// full-resolution photo is still what gets used for generation.
// 768px keeps thin line-art detail (floor plans, drawings) legible to the
// classifier; 512px was blurring the cues that distinguish a plan from a photo.
const DEFAULT_MAX_EDGE = 768;
const DEFAULT_QUALITY = 0.7;

function drawToDataUrl(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxEdge: number,
  quality: number,
): string | null {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

// Preferred path: decode straight from the File/Blob, skipping the expensive
// full-resolution base64 round-trip.
export async function downscaleFile(
  file: Blob,
  maxEdge = DEFAULT_MAX_EDGE,
  quality = DEFAULT_QUALITY,
): Promise<string | null> {
  try {
    if (typeof createImageBitmap !== "function") return null;
    const bitmap = await createImageBitmap(file);
    const out = drawToDataUrl(bitmap, bitmap.width, bitmap.height, maxEdge, quality);
    bitmap.close?.();
    return out;
  } catch {
    return null;
  }
}

export async function downscaleDataUrl(
  dataUrl: string,
  maxEdge = DEFAULT_MAX_EDGE,
  quality = DEFAULT_QUALITY,
): Promise<string> {
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Could not decode image"));
      el.src = dataUrl;
    });

    const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
    if (scale >= 1 && dataUrl.length < 400_000) return dataUrl;

    return (
      drawToDataUrl(img, img.naturalWidth, img.naturalHeight, maxEdge, quality) ?? dataUrl
    );
  } catch {
    return dataUrl;
  }
}
